import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, Clock, MapPin } from "lucide-react";
import { IMG } from "../data/site";

const LINES = ["Bath Fittings,", "Designer Tiles —", "One Fine Address."];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-screen flex-col justify-end overflow-hidden bg-ink">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={IMG.heroShowroom}
          alt="Luxury bathroom fittings showroom"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/40" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/40 to-transparent sm:via-transparent" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-40 pt-48 sm:px-8 lg:px-16">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-8 inline-block max-w-full border border-[#F6F1E7]/25 bg-ink/50 px-5 py-2.5 font-mono text-[11px] uppercase leading-relaxed tracking-[0.2em] text-[#F6F1E7] backdrop-blur-md sm:text-xs sm:tracking-[0.35em]"
          data-testid="hero-eyebrow"
        >
          Jaquar <span className="text-brand-bright">·</span> Kohler <span className="text-brand-bright">·</span> Hindware <span className="text-brand-bright">·</span> Designer Tiles <span className="text-brand-bright">·</span> Pipes &amp; RO Purifiers
        </motion.p>

        <h1 className="font-serif text-4xl font-medium leading-[1.05] tracking-tight text-[#F6F1E7] sm:text-6xl lg:text-7xl" data-testid="hero-headline">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.25 + i * 0.14, ease: [0.215, 0.61, 0.355, 1] }}
              >
                {i === 2 ? (
                  <>
                    One Fine <em className="not-italic text-[#E8694F]">Address.</em>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-[#C7CFD8] sm:text-lg"
          data-testid="hero-subheadline"
        >
          A 50-year family trading legacy, led by Arun Tayal — Jaquar, Kohler and Hindware
          on live display, with designer tiles from Kajaria, Somany, Nitco and more.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/brands"
            data-testid="hero-explore-fittings-button"
            className="group flex items-center gap-3 bg-brand px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-all duration-300 hover:bg-brand-bright"
          >
            Explore Bath Fittings
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/contact"
            data-testid="hero-book-visit-button"
            className="border border-[#F6F1E7]/40 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[#F6F1E7] transition-all duration-300 hover:border-brand-bright hover:text-brand-bright"
          >
            Book Showroom Visit
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="absolute inset-x-0 bottom-0 z-10 border-t border-white/10 bg-ink/50 backdrop-blur-md"
        data-testid="hero-status-bar"
      >
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-[#C7CFD8] sm:px-8 lg:px-16">
          <span className="flex items-center gap-2">
            <Clock size={13} className="text-brand-bright" /> Open Today · 10 AM – 8 PM
          </span>
          <span className="hidden items-center gap-2 md:flex">
            <MapPin size={13} className="text-brand-bright" /> Tigaon Road, Sec-86, Greater Faridabad
          </span>
          <span className="flex items-center gap-2">
            Scroll <ArrowDown size={13} className="animate-bounce text-brand-bright" />
          </span>
        </div>
      </motion.div>
    </section>
  );
};
